import React, { useEffect, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Plus, X, Trash2, AlertTriangle, Zap, Star, MapPin, Clock, Check } from 'lucide-react';
import api from '../../api/client';
import { ORARI } from '../../utils/orari';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';
import useIsMobile from '../../utils/useIsMobile';
import ControlloPlanning, { EsitoControllo, COLORI_CONTROLLO } from './ControlloPlanning';

const MESI_FULL = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno',
                   'Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const GIORNI_LABEL = ['Lun','Mar','Mer','Gio','Ven','Sab','Dom'];

const statoColor = { INSERITA:'#888', CONFERMATA:'#22C55E', SEGNALATA:'#EF4444', DA_APPROVARE:'#F59E0B', RIFIUTATA:'#EF4444' };

const calcolaGiorni = (dataInizio, dataFine, giorniSett) => {
  if (!dataInizio || !dataFine || !giorniSett.length) return 0;
  let n = 0;
  const cur = new Date(dataInizio);
  const fine = new Date(dataFine);
  cur.setHours(0,0,0,0); fine.setHours(23,59,59,999);
  while (cur <= fine) {
    const dow = (cur.getDay() + 6) % 7;
    if (giorniSett.includes(dow)) n++;
    cur.setDate(cur.getDate() + 1);
  }
  return n;
};

const calcolaOre = (oraInizio, oraFine) => {
  const [h1,m1] = oraInizio.split(':').map(Number);
  const [h2,m2] = oraFine.split(':').map(Number);
  return ((h2*60+m2) - (h1*60+m1)) / 60;
};

export default function CalendarioPresenze() {
  const { user } = useAuth();
  const puoCreare    = puoFare(user, 'presenze', 'create');
  const puoEditare   = puoFare(user, 'presenze', 'edit');
  const puoEliminare = puoFare(user, 'presenze', 'delete');
  const oggi = new Date();
  const [anno, setAnno]             = useState(oggi.getFullYear());
  const [mese, setMese]             = useState(oggi.getMonth() + 1);
  const [presenze, setPresenze]     = useState([]);
  const [dipendenti, setDipendenti] = useState([]);
  const [listino, setListino]       = useState([]);
  const [controllo, setControllo]   = useState(null); // controllo planning ↔ presenze del mese
  const [giornoSel, setGiornoSel]   = useState(null);
  // telefono (19/10/2026): mese compatto con pallini, elenco del giorno sotto
  const isMobile = useIsMobile();
  const [loading, setLoading]       = useState(false);
  const [showFormSlot, setShowFormSlot]         = useState(false);
  const [showFormAttivita, setShowFormAttivita] = useState(false);
  const [formTipo, setFormTipo]                 = useState('FISSO'); // 'FISSO' | 'SPOT'

  // Form slot ricorrente
  const [slot, setSlot] = useState({
    dipendente_id: '',
    data_inizio:   '',
    data_fine:     '',
    giorni_settimana: [],
    ora_inizio:    '18:00',
    ora_fine:      '19:30',
    nota:          '',
  });
  const [generating, setGenerating] = useState(false);
  const [generaResult, setGeneraResult] = useState(null);

  // Form presenze extra — attività da listino (FISSO)
  const emptyAtt = { dipendente_id:'', data:'', tipo_attivita_id:'', n_organizzatori:'1', luogo:'', nota:'' };
  const [att, setAtt]             = useState(emptyAtt);
  const [savingAtt, setSavingAtt] = useState(false);

  // Form presenze extra — spot (ora_inizio/ora_fine, senza data fissa)
  const emptySpot = { dipendente_id:'', data:'', luogo:'', ora_inizio:'10:00', ora_fine:'12:00', nota:'' };
  const [spot, setSpot]           = useState(emptySpot);
  const [savingSpot, setSavingSpot] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, dRes, lRes] = await Promise.all([
        api.get(`/dipendenti/presenze/${anno}/${mese}`),
        api.get(`/dipendenti?anno=${anno}`),
        api.get('/dipendenti/listino'),
      ]);
      setPresenze(pRes.data);
      setDipendenti(dRes.data);
      setListino(lRes.data.filter(e => e.attivo));
      api.get(`/dipendenti/controllo-planning/${anno}/${mese}`).then(r => setControllo(r.data)).catch(() => setControllo(null));
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [anno, mese]);

  useEffect(() => { load(); setGiornoSel(null); }, [load]);

  const navMese = (dir) => {
    let m = mese + dir, a = anno;
    if (m > 12) { m = 1; a++; }
    if (m < 1)  { m = 12; a--; }
    setMese(m); setAnno(a);
  };

  const toggleGiorno = (g) => {
    setSlot(p => ({
      ...p,
      giorni_settimana: p.giorni_settimana.includes(g)
        ? p.giorni_settimana.filter(x => x !== g)
        : [...p.giorni_settimana, g],
    }));
    setGeneraResult(null);
  };

  const handleGeneraSlot = async (e) => {
    e.preventDefault();
    setGenerating(true);
    setGeneraResult(null);
    try {
      const r = await api.post('/dipendenti/presenze/genera-slot', { ...slot });
      setGeneraResult(r.data);
      load();
    } catch(e) {
      alert('Errore: ' + (e.response?.data?.error || e.message));
    } finally { setGenerating(false); }
  };

  const handleCreateAttivita = async (e) => {
    e.preventDefault();
    setSavingAtt(true);
    try {
      await api.post('/dipendenti/presenze', { ...att, tipo: 'FISSO' });
      setAtt(emptyAtt);
      setShowFormAttivita(false);
      load();
    } catch(e) {
      alert('Errore: ' + (e.response?.data?.error || e.message));
    } finally { setSavingAtt(false); }
  };

  const handleCreateSpot = async (e) => {
    e.preventDefault();
    setSavingSpot(true);
    try {
      await api.post('/dipendenti/presenze', { ...spot, tipo: 'SPOT' });
      setSpot(emptySpot);
      setShowFormAttivita(false);
      load();
    } catch(e) {
      alert('Errore: ' + (e.response?.data?.error || e.message));
    } finally { setSavingSpot(false); }
  };

  const eliminaPresenza = async (id, stato) => {
    if (!window.confirm(stato === 'CONFERMATA' ? 'Questa presenza è già confermata: eliminandola le ore e il compenso spariscono dal mese. Procedere?' : 'Eliminare questa presenza?')) return;
    try {
      await api.delete(`/dipendenti/presenze/${id}`);
      load();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  // Revisione presenze auto-inserite dall'allenatore (stato DA_APPROVARE)
  const [revisioni, setRevisioni] = useState({});
  const draftRevisione = (p) => revisioni[p.id] || { ora_inizio: p.ora_inizio, ora_fine: p.ora_fine, tipo_attivita_id: p.tipo_attivita_id || '', luogo: p.luogo || '', nota: p.nota || '' };
  const aggiornaRevisione = (p, field, value) => setRevisioni(prev => ({ ...prev, [p.id]: { ...draftRevisione(p), [field]: value } }));

  const approvaPresenza = async (p) => {
    const r = draftRevisione(p);
    try {
      await api.patch(`/dipendenti/presenze/${p.id}/approva`, r);
      setRevisioni(prev => { const n = { ...prev }; delete n[p.id]; return n; });
      load();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const rifiutaPresenza = async (p) => {
    if (!window.confirm(`Rifiutare l'allenamento segnalato da ${p.dipendente?.cognome}?`)) return;
    const r = draftRevisione(p);
    try {
      await api.patch(`/dipendenti/presenze/${p.id}/rifiuta`, { nota: r.nota });
      setRevisioni(prev => { const n = { ...prev }; delete n[p.id]; return n; });
      load();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  // Griglia mese
  const primoGiorno  = new Date(anno, mese - 1, 1);
  const offsetInizio = (primoGiorno.getDay() + 6) % 7;
  const giorniMese   = new Date(anno, mese, 0).getDate();
  const celle = [];
  for (let i = 0; i < offsetInizio; i++) celle.push(null);
  for (let g = 1; g <= giorniMese; g++) celle.push(g);
  while (celle.length % 7 !== 0) celle.push(null);

  const presenzePerGiorno = presenze.reduce((acc, p) => {
    const g = new Date(p.data).getDate();
    if (!acc[g]) acc[g] = [];
    acc[g].push(p);
    return acc;
  }, {});

  const esitoDi = {};
  (controllo?.esiti || []).forEach(e => { esitoDi[e.presenza_id] = e; });
  const fuoriPlanningGiorno = (ps) => {
    const l = ps.map(p => esitoDi[p.id]?.livello);
    return l.includes('allarme') ? 'allarme' : l.includes('attenzione') ? 'attenzione' : null;
  };

  const presenzeGiorno = giornoSel
    ? [...(presenzePerGiorno[giornoSel] || [])].sort((a,b) => {
        if (a.tipo !== b.tipo) return a.tipo === 'ORARIO' ? -1 : 1;
        return (a.ora_inizio || '').localeCompare(b.ora_inizio || '');
      })
    : [];

  const totOre    = presenze.reduce((s, p) => s + (p.ore_lavorate || 0), 0);

  const nGiorniPreview = slot.ora_inizio && slot.ora_fine && calcolaOre(slot.ora_inizio, slot.ora_fine) > 0
    ? calcolaGiorni(slot.data_inizio, slot.data_fine, slot.giorni_settimana) : 0;
  const dipSel = dipendenti.find(d => String(d.id) === String(slot.dipendente_id));
  const oreSlot = slot.ora_inizio && slot.ora_fine ? calcolaOre(slot.ora_inizio, slot.ora_fine) : 0;
  const importoTotPreview = dipSel ? nGiorniPreview * oreSlot * dipSel.tariffa_oraria : 0;

  const dataGiornoSel = giornoSel
    ? new Date(anno, mese - 1, giornoSel).toLocaleDateString('it-IT',{ weekday:'long', day:'numeric', month:'long' })
    : '';

  // Anteprima compenso attività
  const attListinoSel = listino.find(l => String(l.id) === String(att.tipo_attivita_id));
  const nOrg = Math.max(1, parseInt(att.n_organizzatori) || 1);
  const importoAttPreview = attListinoSel
    ? (attListinoSel.divisibile ? attListinoSel.importo / nOrg : attListinoSel.importo)
    : 0;

  const spotDip = dipendenti.find(d => String(d.id) === String(spot.dipendente_id));
  const oreSpot = (spot.ora_inizio && spot.ora_fine) ? calcolaOre(spot.ora_inizio, spot.ora_fine) : 0;
  const importoSpotPreview = spotDip && oreSpot > 0 ? Math.round(oreSpot * spotDip.tariffa_oraria * 100) / 100 : 0;

  const riepilogoPerDip = presenze.reduce((acc, p) => {
    const nome = `${p.dipendente?.cognome} ${p.dipendente?.nome}`;
    if (!acc[nome]) acc[nome] = { ore: 0, importo: 0 };
    acc[nome].ore     += p.ore_lavorate || 0;
    acc[nome].importo += p.importo;
    return acc;
  }, {});

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
        <h1 style={s.title}>CALENDARIO PRESENZE</h1>
        <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
          <button style={s.navBtn} onClick={() => navMese(-1)}><ChevronLeft size={16}/></button>
          <span style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-xl)', fontWeight:'800', minWidth:'180px', textAlign:'center' }}>
            {MESI_FULL[mese-1].toUpperCase()} {anno}
          </span>
          <button style={s.navBtn} onClick={() => navMese(1)}><ChevronRight size={16}/></button>
          {puoCreare && <button style={s.btnAtt} onClick={() => { setShowFormAttivita(v=>!v); setShowFormSlot(false); }}>
            <Star size={13}/> PRESENZE EXTRA
          </button>}
          {puoCreare && <button style={s.btnPri} onClick={() => { setShowFormSlot(v=>!v); setShowFormAttivita(false); setGeneraResult(null); }}>
            <Zap size={13}/> AGGIUNGI SLOT
          </button>}
        </div>
      </div>

      {/* ── FORM PRESENZE EXTRA ── */}
      {showFormAttivita && (
        <div style={{ background:'var(--bg-card,#141414)', border:'1px solid #8B5CF640', borderRadius:'12px', padding:'20px', marginBottom:'20px' }}>
          <div style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight:'800', color:'#8B5CF6', marginBottom:'14px', display:'flex', alignItems:'center', gap:'8px' }}>
            <Star size={16}/> PRESENZE EXTRA
          </div>

          {/* Tab switcher */}
          <div style={{ display:'flex', gap:'6px', marginBottom:'18px' }}>
            {[['FISSO','ATTIVITÀ DA LISTINO'],['SPOT','SPOT / ALLENAMENTO']].map(([t, label]) => (
              <button key={t} type="button"
                style={{ padding:'7px 16px', borderRadius:'8px', border:`1px solid ${formTipo===t?'#8B5CF6':'var(--border2,#2A2A2A)'}`,
                  background:formTipo===t?'#8B5CF620':'var(--bg-input,#1A1A1A)', color:formTipo===t?'#8B5CF6':'#777',
                  fontWeight:'700', fontSize:'var(--font-size-xs)', cursor:'pointer', transition:'all 0.15s' }}
                onClick={() => setFormTipo(t)}>
                {label}
              </button>
            ))}
          </div>

          {/* ── Tab ATTIVITÀ DA LISTINO (FISSO) ── */}
          {formTipo === 'FISSO' && (
            <form onSubmit={handleCreateAttivita}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px', marginBottom:'12px' }}>
                <div>
                  <label style={s.label}>ALLENATORE *</label>
                  <select style={s.inp} value={att.dipendente_id} onChange={e=>setAtt(p=>({...p,dipendente_id:e.target.value}))} required>
                    <option value="">— seleziona —</option>
                    {dipendenti.map(d => <option key={d.id} value={d.id}>{d.cognome} {d.nome}</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>DATA *</label>
                  <input style={s.inp} type="date" value={att.data} onChange={e=>setAtt(p=>({...p,data:e.target.value}))} required/>
                </div>
                <div>
                  <label style={s.label}>ATTIVITÀ *</label>
                  <select style={s.inp} value={att.tipo_attivita_id} onChange={e=>setAtt(p=>({...p,tipo_attivita_id:e.target.value}))} required>
                    <option value="">— seleziona —</option>
                    {listino.map(l => <option key={l.id} value={l.id}>{l.nome} — €{l.importo.toFixed(2)}{l.divisibile?' (div.)':''}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns: attListinoSel?.divisibile ? '1fr 1fr 2fr' : '1fr 2fr', gap:'12px', marginBottom:'12px' }}>
                <div>
                  <label style={s.label}><MapPin size={10} style={{marginRight:'3px'}}/>LUOGO</label>
                  <input style={s.inp} placeholder="es. Bione, Lungolago..." value={att.luogo} onChange={e=>setAtt(p=>({...p,luogo:e.target.value}))}/>
                </div>
                {attListinoSel?.divisibile && (
                  <div>
                    <label style={s.label}>N° ORGANIZZATORI</label>
                    <input style={s.inp} type="number" min="1" max="20" value={att.n_organizzatori}
                      onChange={e=>setAtt(p=>({...p,n_organizzatori:e.target.value}))}/>
                  </div>
                )}
                <div>
                  <label style={s.label}>NOTE</label>
                  <input style={s.inp} placeholder="Opzionale" value={att.nota} onChange={e=>setAtt(p=>({...p,nota:e.target.value}))}/>
                </div>
              </div>

              {attListinoSel && (
                <div style={{ background:'#8B5CF610', border:'1px solid #8B5CF630', borderRadius:'8px', padding:'12px 16px', marginBottom:'14px', fontSize:'var(--font-size-sm)' }}>
                  <span style={{ color:'#8B5CF6', fontWeight:'700' }}>Anteprima: </span>
                  {attListinoSel.nome}
                  {att.luogo && <span style={{ color:'#777' }}> · {att.luogo}</span>}
                  {attListinoSel.divisibile && nOrg > 1 ? ` ÷ ${nOrg} organizzatori` : ''}
                  {' → '}<strong style={{ color:'#8B5CF6' }}>€{importoAttPreview.toFixed(2)}</strong>
                  {attListinoSel.descrizione && <div style={{ color:'#666', fontSize:'var(--font-size-xs)', marginTop:'4px' }}>{attListinoSel.descrizione}</div>}
                </div>
              )}

              <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
                <button type="button" style={s.btnSec} onClick={() => { setShowFormAttivita(false); setAtt(emptyAtt); }}>ANNULLA</button>
                <button type="submit" style={{ ...s.btnPri, background:'#8B5CF6' }} disabled={savingAtt}>
                  <Star size={13}/> {savingAtt ? 'SALVO...' : 'INSERISCI'}
                </button>
              </div>
            </form>
          )}

          {/* ── Tab SPOT ── */}
          {formTipo === 'SPOT' && (
            <form onSubmit={handleCreateSpot}>
              <div style={{ fontSize:'var(--font-size-xs)', color:'#888', marginBottom:'14px' }}>
                Pre-tappa Club Series, open day e allenamenti una-tantum — stessa tariffa oraria, data non fissa.
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'12px', marginBottom:'12px' }}>
                <div>
                  <label style={s.label}>ALLENATORE *</label>
                  <select style={s.inp} value={spot.dipendente_id} onChange={e=>setSpot(p=>({...p,dipendente_id:e.target.value}))} required>
                    <option value="">— seleziona —</option>
                    {dipendenti.map(d => <option key={d.id} value={d.id}>{d.cognome} {d.nome} (€{d.tariffa_oraria}/h)</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>DATA *</label>
                  <input style={s.inp} type="date" value={spot.data} onChange={e=>setSpot(p=>({...p,data:e.target.value}))} required/>
                </div>
                <div>
                  <label style={s.label}>ORA INIZIO *</label>
                  <select style={s.inp} value={spot.ora_inizio} onChange={e=>setSpot(p=>({...p,ora_inizio:e.target.value}))} required>
                    {ORARI.slice(0,-1).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label style={s.label}>ORA FINE *</label>
                  <select style={s.inp} value={spot.ora_fine} onChange={e=>setSpot(p=>({...p,ora_fine:e.target.value}))} required>
                    {ORARI.slice(1).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr', gap:'12px', marginBottom:'12px' }}>
                <div>
                  <label style={s.label}><MapPin size={10} style={{marginRight:'3px'}}/>LUOGO</label>
                  <input style={s.inp} placeholder="es. Bione, Lungolago..." value={spot.luogo} onChange={e=>setSpot(p=>({...p,luogo:e.target.value}))}/>
                </div>
                <div>
                  <label style={s.label}>NOTE</label>
                  <input style={s.inp} placeholder="es. Pre-tappa Club Series, Open Day..." value={spot.nota} onChange={e=>setSpot(p=>({...p,nota:e.target.value}))}/>
                </div>
              </div>

              {spotDip && oreSpot > 0 && (
                <div style={{ background:'#8B5CF610', border:'1px solid #8B5CF630', borderRadius:'8px', padding:'12px 16px', marginBottom:'14px', fontSize:'var(--font-size-sm)' }}>
                  <span style={{ color:'#8B5CF6', fontWeight:'700' }}>Anteprima: </span>
                  {oreSpot.toFixed(2)}h × €{spotDip.tariffa_oraria}/h
                  {spot.luogo && <span style={{ color:'#777' }}> · {spot.luogo}</span>}
                  {' → '}<strong style={{ color:'#8B5CF6' }}>€{importoSpotPreview.toFixed(2)}</strong>
                </div>
              )}

              <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
                <button type="button" style={s.btnSec} onClick={() => { setShowFormAttivita(false); setSpot(emptySpot); }}>ANNULLA</button>
                <button type="submit" style={{ ...s.btnPri, background:'#8B5CF6' }} disabled={savingSpot}>
                  <Zap size={13}/> {savingSpot ? 'SALVO...' : 'INSERISCI SPOT'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* ── FORM GENERA SLOT ── */}
      {showFormSlot && (
        <form onSubmit={handleGeneraSlot} style={{ background:'var(--bg-card,#141414)', border:'1px solid #F4600C40', borderRadius:'12px', padding:'20px', marginBottom:'20px' }}>
          <div style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight:'800', color:'#F4600C', marginBottom:'16px', display:'flex', alignItems:'center', gap:'8px' }}>
            <Zap size={16}/> AGGIUNGI SLOT RICORRENTE
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px', marginBottom:'14px' }}>
            <div>
              <label style={s.label}>ALLENATORE *</label>
              <select style={s.inp} value={slot.dipendente_id} onChange={e=>setSlot(p=>({...p,dipendente_id:e.target.value}))} required>
                <option value="">— seleziona —</option>
                {dipendenti.map(d => <option key={d.id} value={d.id}>{d.cognome} {d.nome} (€{d.tariffa_oraria}/h)</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>DATA INIZIO *</label>
              <input style={s.inp} type="date" value={slot.data_inizio} onChange={e=>setSlot(p=>({...p,data_inizio:e.target.value}))} required/>
            </div>
            <div>
              <label style={s.label}>DATA FINE *</label>
              <input style={s.inp} type="date" value={slot.data_fine} onChange={e=>setSlot(p=>({...p,data_fine:e.target.value}))} required/>
            </div>
          </div>

          <div style={{ marginBottom:'14px' }}>
            <label style={s.label}>GIORNI DELLA SETTIMANA *</label>
            <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
              {GIORNI_LABEL.map((g, i) => {
                const sel = slot.giorni_settimana.includes(i);
                return (
                  <button key={i} type="button"
                    style={{ padding:'8px 14px', borderRadius:'8px', border:`1px solid ${sel?'#F4600C':'var(--border2,#2A2A2A)'}`,
                      background: sel?'#F4600C20':'var(--bg-input,#1A1A1A)', color: sel?'#F4600C':'#777',
                      fontWeight:'700', fontSize: 'var(--font-size-xs)', cursor:'pointer', transition:'all 0.15s' }}
                    onClick={() => toggleGiorno(i)}>
                    {g}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px', marginBottom:'14px' }}>
            <div>
              <label style={s.label}>ORA INIZIO *</label>
              <select style={s.inp} value={slot.ora_inizio} onChange={e=>setSlot(p=>({...p,ora_inizio:e.target.value}))}>
                {ORARI.slice(0,-1).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>ORA FINE *</label>
              <select style={s.inp} value={slot.ora_fine} onChange={e=>setSlot(p=>({...p,ora_fine:e.target.value}))}>
                {ORARI.slice(1).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label style={s.label}>NOTE</label>
              <input style={s.inp} placeholder="es. under 18, amatori..." value={slot.nota} onChange={e=>setSlot(p=>({...p,nota:e.target.value}))}/>
            </div>
          </div>

          {nGiorniPreview > 0 && dipSel && oreSlot > 0 && (
            <div style={{ background:'#F4600C10', border:'1px solid #F4600C30', borderRadius:'8px', padding:'12px 16px', marginBottom:'14px', fontSize: 'var(--font-size-sm)' }}>
              <span style={{ color:'#F4600C', fontWeight:'700' }}>Anteprima: </span>
              <strong>{nGiorniPreview}</strong> presenze ·{' '}
              <strong>{(nGiorniPreview * oreSlot).toFixed(2)}h</strong> totali ·{' '}
              <strong style={{ color:'#F4600C' }}>€{importoTotPreview.toFixed(2)}</strong> totali per {dipSel.cognome} {dipSel.nome}
            </div>
          )}

          {generaResult && (
            <div style={{ background:'#22C55E10', border:'1px solid #22C55E40', borderRadius:'8px', padding:'12px 16px', marginBottom:'14px', fontSize: 'var(--font-size-sm)', color:'#22C55E' }}>
              ✓ Generate <strong>{generaResult.generate}</strong> presenze ·{' '}
              <strong>{generaResult.ore_totali}h</strong> · <strong>€{generaResult.importo_totale.toFixed(2)}</strong>
            </div>
          )}

          <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
            <button type="button" style={s.btnSec} onClick={() => { setShowFormSlot(false); setGeneraResult(null); }}>ANNULLA</button>
            <button type="submit" style={s.btnPri} disabled={generating || !slot.giorni_settimana.length}>
              <Zap size={13}/> {generating ? 'GENERO...' : 'GENERA PRESENZE'}
            </button>
          </div>
        </form>
      )}

      <ControlloPlanning controllo={controllo} puoEditare={puoEditare} onSalvato={load}
        onGiorno={(g) => { setGiornoSel(g); setTimeout(() => document.getElementById('pannello-giorno')?.scrollIntoView({ behavior:'smooth', block:'start' }), 50); }} />

      <div style={{ display:'grid', gridTemplateColumns: giornoSel && !isMobile ? '1fr 340px' : 'minmax(0,1fr)', gap:'20px', alignItems:'start' }}>

        {/* ── GRIGLIA ── */}
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,minmax(0,1fr))', gap: isMobile ? '3px' : '4px', marginBottom:'4px' }}>
            {GIORNI_LABEL.map(g => (
              <div key={g} style={{ textAlign:'center', fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'1px', color:'#555', padding:'4px 0' }}>{g}</div>
            ))}
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,minmax(0,1fr))', gap: isMobile ? '3px' : '4px' }}>
            {celle.map((giorno, i) => {
              if (!giorno) return <div key={i}/>;
              const ps      = presenzePerGiorno[giorno] || [];
              const isOggi  = giorno===oggi.getDate() && mese===oggi.getMonth()+1 && anno===oggi.getFullYear();
              const isSel   = giorno === giornoSel;
              const haProbl = ps.some(p => p.stato === 'SEGNALATA');
              const haDaApprovare = ps.some(p => p.stato === 'DA_APPROVARE');
              const fuori = fuoriPlanningGiorno(ps);

              if (isMobile) return (
                <div key={i} onClick={() => { setGiornoSel(isSel ? null : giorno); if (!isSel) setTimeout(() => document.getElementById('pannello-giorno')?.scrollIntoView({ behavior:'smooth', block:'start' }), 50); }}
                  style={{ background: isSel ? '#F4600C' : 'var(--bg-card,#fff)', border:`1px solid ${isOggi && !isSel ? '#F4600C' : 'transparent'}`, borderRadius:'10px', padding:'6px 2px', minHeight:'54px', cursor:'pointer', display:'flex', flexDirection:'column', alignItems:'center', gap:'4px' }}>
                  <span style={{ fontSize:'14px', fontWeight:700, color: isSel ? '#fff' : isOggi ? '#F4600C' : 'var(--text)' }}>{giorno}</span>
                  {ps.length > 0 && (
                    <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'center', gap:'2px', maxWidth:'34px' }}>
                      {ps.slice(0, 6).map(p => {
                        const col = p.stato==='SOSTITUITA' ? '#999' : p.stato==='DA_APPROVARE' ? '#F59E0B' : p.stato==='RIFIUTATA' ? '#EF4444' : (p.tipo==='FISSO' || p.tipo==='SPOT') ? '#8B5CF6' : statoColor[p.stato];
                        return <span key={p.id} style={{ width:'6px', height:'6px', borderRadius:'50%', background: isSel ? '#fff' : col }}/>;
                      })}
                    </div>
                  )}
                  {ps.length > 6 && <span style={{ fontSize:'10px', color: isSel ? '#fff' : 'var(--text-dim)' }}>+{ps.length - 6}</span>}
                  {(haDaApprovare || haProbl) && !isSel && <span style={{ fontSize:'9px', lineHeight:1, color: haProbl ? '#EF4444' : '#F59E0B' }}>●</span>}
                  {fuori && <span title="Ore fuori dai gruppi del planning" style={{ fontSize:'9px', lineHeight:1, fontWeight:800, color: isSel ? '#fff' : COLORI_CONTROLLO[fuori] }}>⚠</span>}
                </div>
              );
              return (
                <div key={i} onClick={() => setGiornoSel(isSel ? null : giorno)}
                  style={{ background: isSel?'#F4600C15':'var(--bg-card,#141414)',
                    border:`1px solid ${isSel?'#F4600C':isOggi?'#F4600C50':'var(--border,#1E1E1E)'}`,
                    borderRadius:'8px', padding:'8px', minHeight:'70px', cursor:'pointer', transition:'all 0.15s' }}
                  onMouseEnter={e=>{ if(!isSel) e.currentTarget.style.borderColor='#F4600C40'; }}
                  onMouseLeave={e=>{ if(!isSel) e.currentTarget.style.borderColor=isOggi?'#F4600C50':'var(--border,#1E1E1E)'; }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'4px' }}>
                    <span style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', color:isSel||isOggi?'#F4600C':'var(--text,#fff)' }}>{giorno}</span>
                    <div style={{ display:'flex', gap:'3px' }}>
                      {fuori && <span title="Ore fuori dai gruppi del planning: vedi CONTROLLO PLANNING"><AlertTriangle size={10} color={COLORI_CONTROLLO[fuori]}/></span>}
                      {haDaApprovare && <Clock size={10} color="#F59E0B"/>}
                      {haProbl && <AlertTriangle size={10} color="#EF4444"/>}
                    </div>
                  </div>
                  {ps.length > 0 && (
                    <div style={{ display:'flex', flexDirection:'column', gap:'2px' }}>
                      {ps.slice(0,2).map(p => {
                        const isExtra = p.tipo==='FISSO' || p.tipo==='SPOT';
                        const isSostituita = p.stato==='SOSTITUITA';
                        const isPending = p.stato==='DA_APPROVARE';
                        const isRifiutata = p.stato==='RIFIUTATA';
                        return (
                          <div key={p.id} style={{ fontSize: 'var(--font-size-xs)',
                            background: isSostituita ? '#55555520' : isPending ? '#F59E0B20' : isRifiutata ? '#EF444420' : isExtra ? '#8B5CF620' : statoColor[p.stato]+'20',
                            color: isSostituita ? '#555' : isPending ? '#F59E0B' : isRifiutata ? '#EF4444' : isExtra ? '#8B5CF6' : statoColor[p.stato],
                            borderRadius:'3px', padding:'1px 4px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontWeight:'600' }}>
                            {p.dipendente?.cognome} {p.tipo==='FISSO' ? (p.tipo_attivita?.nome || '★') : p.stato==='SOSTITUITA' ? '✕' : p.ora_inizio}
                          </div>
                        );
                      })}
                      {ps.length > 2 && <div style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>+{ps.length-2}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Riepilogo */}
          {presenze.length > 0 && (
            <div style={{ marginTop:'16px', background:'var(--bg-card,#141414)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'12px', padding:'14px 18px' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'10px' }}>
                RIEPILOGO {MESI_FULL[mese-1].toUpperCase()} {anno}
              </div>
              <div style={{ display:'flex', gap:'20px', flexWrap:'wrap', alignItems:'flex-end' }}>
                {Object.entries(riepilogoPerDip).sort().map(([nome, r]) => (
                  <div key={nome}>
                    <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'600', marginBottom:'2px' }}>{nome}</div>
                    <div style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight:'800', color:'#F4600C' }}>€{Math.round(r.importo)}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>{r.ore.toFixed(2)}h</div>
                  </div>
                ))}
                <div style={{ marginLeft:'auto', textAlign:'right' }}>
                  <div style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-xl)', fontWeight:'800' }}>€{Object.values(riepilogoPerDip).reduce((s, r) => s + Math.round(r.importo), 0)}</div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>{totOre.toFixed(2)}h · {presenze.length} presenze</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── PANNELLO GIORNO ── */}
        {giornoSel && (
          <div id="pannello-giorno" style={{ background:'var(--bg-card,#141414)', border:'1px solid #F4600C40', borderRadius:'12px', overflow:'hidden', position: isMobile ? 'static' : 'sticky', top:'20px', scrollMarginTop:'70px' }}>
            <div style={{ padding:'12px 16px', background:'#F4600C15', borderBottom:'1px solid #F4600C30', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-sm)', fontWeight:'800', color:'#F4600C', textTransform:'uppercase' }}>{dataGiornoSel}</div>
                <div style={{ fontSize: 'var(--font-size-xs)', color:'#888' }}>
                  {presenzeGiorno.length} {presenzeGiorno.length===1?'presenza':'presenze'}
                  {presenzeGiorno.filter(p=>p.tipo==='ORARIO').length > 0 && (
                    <> · {presenzeGiorno.filter(p=>p.tipo==='ORARIO').reduce((s,p)=>s+(p.ore_lavorate||0),0).toFixed(2)}h</>
                  )}
                </div>
              </div>
              <button style={{ ...s.navBtn, padding:'6px' }} onClick={() => setGiornoSel(null)}><X size={14}/></button>
            </div>

            <div style={{ padding:'12px 16px', display:'flex', flexDirection:'column', gap:'8px', maxHeight: isMobile ? 'none' : '420px', overflowY: isMobile ? 'visible' : 'auto' }}>
              {presenzeGiorno.length === 0 ? (
                <p style={{ textAlign:'center', color:'#555', fontSize: 'var(--font-size-xs)', padding:'20px 0' }}>
                  Nessuna presenza in questo giorno.<br/>
                  <span style={{ fontSize: 'var(--font-size-xs)' }}>Usa i pulsanti in alto per inserire.</span>
                </p>
              ) : presenzeGiorno.map(p => {
                const isFisso     = p.tipo === 'FISSO';
                const isSpot      = p.tipo === 'SPOT';
                const isSostituita = p.stato === 'SOSTITUITA';
                const isSostituzione = !!p.sostituzione_di_id;
                const isPending   = p.stato === 'DA_APPROVARE';
                const isRifiutata = p.stato === 'RIFIUTATA';
                const sc = isSostituita ? '#444' : isPending ? '#F59E0B' : isRifiutata ? '#EF4444' : (isFisso || isSpot) ? '#8B5CF6' : (statoColor[p.stato] || '#888');
                const draft = isPending ? draftRevisione(p) : null;
                return (
                  <div key={p.id} style={{ background:'var(--bg-input,#1A1A1A)', border:`1px solid ${sc}30`, borderRadius:'8px', padding:'10px 12px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:'700', fontSize: 'var(--font-size-sm)' }}>{p.dipendente?.cognome} {p.dipendente?.nome}</div>
                        {isFisso ? (
                          <div style={{ fontSize: 'var(--font-size-xs)', color:'#8B5CF6', fontWeight:'600', marginTop:'2px', display:'flex', alignItems:'center', gap:'4px' }}>
                            <Star size={10}/> {p.tipo_attivita?.nome || 'Attività'}
                          </div>
                        ) : isSpot ? (
                          <div style={{ fontSize: 'var(--font-size-xs)', color:'#8B5CF6', fontWeight:'600', marginTop:'2px', display:'flex', alignItems:'center', gap:'4px' }}>
                            <Zap size={10}/> {p.ora_inizio} – {p.ora_fine}
                          </div>
                        ) : (
                          <div style={{ fontSize: 'var(--font-size-xs)', color: isSostituita?'#555':'#555', fontFamily:'monospace', textDecoration: isSostituita?'line-through':'' }}>{p.ora_inizio} – {p.ora_fine}</div>
                        )}
                        {isSostituita && p.sostituzione && (
                          <div style={{ fontSize:'var(--font-size-xs)', color:'#EF4444', marginTop:'2px' }}>
                            ↪ Sost. da: {p.sostituzione.dipendente?.cognome} {p.sostituzione.dipendente?.nome}
                          </div>
                        )}
                        {isSostituzione && p.sostituzione_di && (
                          <div style={{ fontSize:'var(--font-size-xs)', color:'#8B5CF6', marginTop:'2px' }}>
                            ↩ Sub di: {p.sostituzione_di.dipendente?.cognome} {p.sostituzione_di.dipendente?.nome}
                          </div>
                        )}
                        {p.luogo && <div style={{ fontSize: 'var(--font-size-xs)', color:'#777', marginTop:'2px', display:'flex', alignItems:'center', gap:'3px' }}><MapPin size={9}/>{p.luogo}</div>}
                        {p.nota && <div style={{ fontSize: 'var(--font-size-xs)', color:'#888', marginTop:'2px' }}>{p.nota}</div>}
                        {esitoDi[p.id] && esitoDi[p.id].livello !== 'ok' && (
                          <div style={{ marginTop:'6px' }}><EsitoControllo e={esitoDi[p.id]} puoEditare={puoEditare} onSalvato={load} compatto /></div>
                        )}
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0, marginLeft:'8px' }}>
                        <div style={{ fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-lg)', fontWeight:'800', color: isSostituita?'#555':(isFisso||isSpot)?'#8B5CF6':'#F4600C' }}>
                          {isSostituita ? <span style={{ textDecoration:'line-through', color:'#555' }}>€{p.importo.toFixed(2)}</span> : `€${p.importo.toFixed(2)}`}
                        </div>
                        {(!isFisso) && <div style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>{(p.ore_lavorate||0).toFixed(2)}h</div>}
                      </div>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:'6px' }}>
                      <span style={{ fontSize: 'var(--font-size-xs)', fontWeight:'800', padding:'2px 6px', borderRadius:'3px', color:sc, background:sc+'20', display:'flex', alignItems:'center', gap:'4px' }}>
                        {isPending && <Clock size={9}/>}
                        {isPending ? 'DA APPROVARE' : isRifiutata ? 'RIFIUTATA' : isFisso ? 'EXTRA' : isSpot ? 'SPOT' : isSostituita ? 'SOSTITUITA' : isSostituzione ? 'SUB' : p.stato}
                      </span>
                      {p.nota_allenatore && <span style={{ fontSize: 'var(--font-size-xs)', color:'#EF4444' }} title={p.nota_allenatore}><AlertTriangle size={10}/></span>}
                      {puoEliminare && <button style={{ background:'transparent', border:'none', cursor:'pointer', color:'#EF4444', padding:'2px', display:'flex', alignItems:'center' }}
                        onClick={() => eliminaPresenza(p.id, p.stato)} title="Elimina">
                        <Trash2 size={13}/>
                      </button>}
                    </div>

                    {/* Auto-inserita dall'allenatore: revisione con eventuali correzioni prima di approvare/rifiutare */}
                    {puoEditare && isPending && (
                      <div style={{ marginTop:'8px', paddingTop:'8px', borderTop:'1px solid #F59E0B30', display:'flex', flexDirection:'column', gap:'6px' }}>
                        {isFisso ? (
                          <select style={{ ...s.miniInp }} value={draft.tipo_attivita_id} onChange={e=>aggiornaRevisione(p,'tipo_attivita_id',e.target.value)}>
                            {listino.map(l => <option key={l.id} value={l.id}>{l.nome} — €{l.importo.toFixed(2)}</option>)}
                          </select>
                        ) : (
                          <div style={{ display:'flex', gap:'6px' }}>
                            <select style={{ ...s.miniInp, flex:1 }} value={draft.ora_inizio} onChange={e=>aggiornaRevisione(p,'ora_inizio',e.target.value)}>
                              {ORARI.slice(0,-1).map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                            <select style={{ ...s.miniInp, flex:1 }} value={draft.ora_fine} onChange={e=>aggiornaRevisione(p,'ora_fine',e.target.value)}>
                              {ORARI.slice(1).map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </div>
                        )}
                        <input style={s.miniInp} placeholder="Luogo" value={draft.luogo} onChange={e=>aggiornaRevisione(p,'luogo',e.target.value)}/>
                        <input style={s.miniInp} placeholder="Nota (visibile allo staff)" value={draft.nota} onChange={e=>aggiornaRevisione(p,'nota',e.target.value)}/>
                        <div style={{ display:'flex', gap:'6px' }}>
                          <button style={{ ...s.btnApprova, flex:1 }} onClick={() => approvaPresenza(p)}>
                            <Check size={12}/> APPROVA
                          </button>
                          <button style={{ ...s.btnRifiuta, flex:1 }} onClick={() => rifiutaPresenza(p)}>
                            RIFIUTA
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  title:   { fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight:'800', letterSpacing:'2px' },
  navBtn:  { background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'8px 10px', color:'var(--text,#fff)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' },
  btnPri:  { display:'flex', alignItems:'center', gap:'6px', background:'#F4600C', color:'#fff', border:'none', borderRadius:'8px', padding:'9px 14px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  btnAtt:  { display:'flex', alignItems:'center', gap:'6px', background:'#8B5CF6', color:'#fff', border:'none', borderRadius:'8px', padding:'9px 14px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  btnSec:  { display:'flex', alignItems:'center', gap:'6px', background:'var(--bg-input,#1A1A1A)', color:'var(--text,#fff)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'9px 14px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  label:   { fontSize: 'var(--font-size-xs)', letterSpacing:'2px', fontWeight:'700', color:'#555', display:'block', marginBottom:'4px' },
  inp:     { background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'8px 12px', color:'var(--text,#fff)', fontSize: 'var(--font-size-sm)', outline:'none', width:'100%', boxSizing:'border-box' },
  miniInp: { background:'var(--bg-card,#141414)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'6px', padding:'6px 8px', color:'var(--text,#fff)', fontSize: 'var(--font-size-xs)', outline:'none', width:'100%', boxSizing:'border-box' },
  btnApprova: { display:'flex', alignItems:'center', justifyContent:'center', gap:'4px', background:'#22C55E20', color:'#22C55E', border:'1px solid #22C55E40', borderRadius:'6px', padding:'6px 10px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  btnRifiuta: { display:'flex', alignItems:'center', justifyContent:'center', background:'#EF444420', color:'#EF4444', border:'1px solid #EF444440', borderRadius:'6px', padding:'6px 10px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
};
